import { Action, createReducer, on } from '@ngrx/store';
import * as PtCoreActions from '../actions/pt-core.actions';
import { LoggedUser, LoginAttempt, SessionNotification } from '@pt/pt-models';
import { LegalEntity, StorageInfo } from '@primatoo-ms-organization';
import { PtCoreUtils } from '../../utils/pt-core.utils';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';
import { PrimatooModule } from 'it/primatoo/openapi/api/org/schemas/common/PrimatooModule';

export const CORE_FEATURE_KEY = 'pt-core';

export interface CoreState {
  anyPermission: boolean;
  loginResponse: LoggedUser;
  organization: Organization;
  organizationStorageInfo: StorageInfo;
  legalEntity: LegalEntity[];
  selectedLegalEntity: LegalEntity[];
  loginAttempt: LoginAttempt[];
  pendingSystemLogin: boolean;
  sessionNotifications: {
    data: SessionNotification[],
    open: boolean,
    id: number
  },
  resolutionScaled: boolean;
  loginModules: PrimatooModule[];
}

export const initialState: CoreState = {
  anyPermission: true,
  loginResponse: null,
  organization: null,
  organizationStorageInfo: null,
  selectedLegalEntity: [],
  loginAttempt: [],
  legalEntity: [],
  pendingSystemLogin: false,
  sessionNotifications: {
    data: [],
    open: false,
    id: 0,
  },
  resolutionScaled: null,
  loginModules: [],
};

export function coreReducer(state: CoreState | undefined, action: Action) {

  const PtCoreReducer = createReducer(
    initialState,

    on(PtCoreActions.testChangePermission, (state, action) => ({
      ...state,
      organization: {
        ...state.organization,
        activeModules: action.permission.activeModules,
      },
      loginResponse: {
        ...state.loginResponse,
        enumeratedPermissions: action.permission.activePermissions,
      },
    })),

    on(PtCoreActions.setLoginModulesResponse, (state, action) => ({
      ...state,
      loginModules: action.loginModules,
    })),

    on(PtCoreActions.setResolutionScaled, (state, action) => ({
      ...state,
      resolutionScaled: action.resolutionScaled,
    })),

    on(PtCoreActions.logoutAndClearDataForInactivityTime, (state) => ({
      ...state,
      sessionNotifications: {
        data: [],
        open: false,
        id: 0,
      },
      loginResponse: null,
      organization: null,
      selectedLegalEntity: [],
      loginAttempt: [],
      legalEntity: [],
      resolutionScaled: null,
    })),

    on(PtCoreActions.logoutAndClearData, () => ({
      ...JSON.parse(JSON.stringify(initialState)),
    })),

    on(PtCoreActions.cleanData, (state) => ({
      ...state,
      sessionNotifications: {
        data: [],
        open: false,
        id: 0,
      },
      pendingSystemLogin: false,
      loginResponse: null,
      organization: null,
      organizationStorageInfo: null,
      selectedLegalEntity: [],
      loginAttempt: [],
      legalEntity: [],
    })),

    on(PtCoreActions.loginFailed, (state, action) => ({
      ...state,
      sessionNotifications: {
        data: [],
        open: false,
        id: 0,
      },
      loginResponse: null,
      organization: null,
      pendingSystemLogin: false,
      loginAttempt: PtCoreUtils.upsertLoginAttempt(
        action.username,
        state.loginAttempt
      ),
    })),

    on(PtCoreActions.loginSuccess, (state, action) => ({
      ...state,
      pendingSystemLogin: false,
      loginResponse: action.loginResponse,
      loginAttempt: state.loginAttempt.filter(
        (a) => a.username !== action.loginResponse.username
      ),
    })),

    on(PtCoreActions.loginToConsoleSuccess, (state, action) => ({
      ...state,
      pendingSystemLogin: false,
      loginResponse: action.loginResponse,
      loginAttempt: state.loginAttempt.filter(
        (a) => a.username !== action.loginResponse.username
      ),
    })),

    on(PtCoreActions.loginAsSystemSuccess, (state, action) => ({
      ...state,
      loginResponse: action.loginResponse,
      loginAttempt: state.loginAttempt.filter(
        (a) => a.username !== action.loginResponse.username
      ),
      pendingSystemLogin: true,
    })),

    on(PtCoreActions.setPendingSystemLoginTo, (state, action) => ({
      ...state,
      pendingSystemLogin: action.pendingSystemLogin,
    })),

    on(PtCoreActions.connectAsUserSuccess, (state, action) => ({
      ...state,
      loginResponse: action.loginResponse,
    })),

    on(PtCoreActions.updateOrganizationCssSuccess, (state, action) => ({
      ...state,
      organization: {
        ...this.state.organization,
        css: action.response.css,
        // resellerLogo: action.response.resellerLogo ? action.response.resellerLogo : 'assets/images/primatoo-logo.png'
      },
    })),

    on(PtCoreActions.getOrganizationSuccess, (state, action) => ({
      ...state,
      organization: {
        ...action.organization,
        resellerLogo: action.organization.resellerLogo
          ? action.organization.resellerLogo
          : 'assets/images/primatoo-logo.png',
      },
    })),

    on(PtCoreActions.getOrganizationStorageInfoSuccess, (state, action) => ({
      ...state,
      organizationStorageInfo: action.storageInfo,
    })),

    on(PtCoreActions.getLegalEntity, (state) => ({
      ...state,
      legalEntity: [],
      selectedLegalEntity: [],
    })),

    on(PtCoreActions.getLegalEntitySuccess, (state, action) => ({
      ...state,
      legalEntity: action.legalEntity ? action.legalEntity : [],
    })),

    on(PtCoreActions.setAnyPermission, (state, action) => ({
      ...state,
      anyPermission: action.anyPermission,
    })),

    on(PtCoreActions.selectLegalEntity, (state, action) => ({
      ...state,
      selectedLegalEntity: action.selectedLegalEntity
        ? action.selectedLegalEntity
        : [],
    })),

    on(PtCoreActions.updateLoginResponse, (state, action) => ({
      ...state,
      loginResponse: {
        ...state.loginResponse,
        language: action.user.preferredLanguage,
        name: action.user.name,
        surname: action.user.surname,
      },
    })),

    on(PtCoreActions.openSessionNotifications, (state) => ({
      ...state,
      sessionNotifications: {
        ...state.sessionNotifications,
        open: true,
      },
    })),

    on(PtCoreActions.closeSessionNotifications, (state) => ({
      ...state,
      sessionNotifications: {
        ...state.sessionNotifications,
        open: false,
      },
    })),

    on(PtCoreActions.addSessionNotifications, (state, action) => ({
      ...state,
      sessionNotifications: {
        ...state.sessionNotifications,
        data: [action.sessionNotification, ...state.sessionNotifications.data],
      },
    })),

    on(PtCoreActions.removeSessionNotifications, (state, action) => ({
      ...state,
      sessionNotifications: {
        ...state.sessionNotifications,
        data: state.sessionNotifications.data.filter(
          (s) => s?.id !== action?.sessionNotificationId
        ),
      },
    })),

    on(PtCoreActions.removeAllSessionNotifications, (state) => ({
      ...state,
      sessionNotifications: {
        ...state.sessionNotifications,
        data: [],
      },
    })),

    on(PtCoreActions.updateDownloadSessionNotifications, (state, action) => ({
      ...state,
      sessionNotifications: {
        ...state.sessionNotifications,
        data: action.sessionNotifications,
      },
    }))
  );

  return PtCoreReducer(state, action);
}
